import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useAtom } from "jotai";
import { enqueueSnackbar } from "notistack";
import Papa from "papaparse";
import React, { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Controller, ValidateResult, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { SysAccountCreateDto } from "@/shared/api";
import { meStore } from "@/store/store";

const ImportAccountsDialog = ({
  title,
  onCancel,
  isOnSaveResponsePending = false,
  onSave,
}: {
  title?: string;
  isOnSaveResponsePending?: boolean;
  onCancel: () => void;
  onSave: (csvFile: File) => void;
}) => {
  const { t } = useTranslation();
  const [me] = useAtom(meStore);
  const { data: authGroupsRs, isLoading: authGroupsIsLoading } = useAuthGroupsQuery();

  const authGroupsOptions = useMemo(() => {
    return authGroupsRs?.data.map((i) => i.name).sort((a, b) => a.localeCompare(b));
  }, [authGroupsRs?.data]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm<{ authGroups: string; csv: string }>();

  const [csvData, setCsvData] = useState<SysAccountCreateDto[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [rawFile, setRawFile] = useState<File | null>(null);

  function handleSaveClick(data: any) {
    if (!rawFile) {
      enqueueSnackbar(t("fileIsRequired"), { variant: "error" });
      return;
    }

    onSave(rawFile);
  }

  const handleFileChange = (file: File) => {
    if (file) {
      const isCsv = file.type === "text/csv";
      if (!isCsv) {
        enqueueSnackbar(t("uploadOnlyCsv"), { variant: "error" });
        return false;
      }

      const isLt10Mb = file.size / 1024 / 1024 < 10;
      if (!isLt10Mb) {
        enqueueSnackbar(t("fileMustSmaller"), { variant: "error" });
        return false;
      }

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          setCsvData(result.data as SysAccountCreateDto[]);
          setRawFile(file);
          setModalOpen(true);

          if (Array.isArray(result.data)) {
            console.log("CSV file read successfully:", result.data);
          }
        },
        error: (err) => {
          console.log("Error reading CSV file:", err);
          enqueueSnackbar(t("Error reading CSV file: " + err), { variant: "error" });
        },
      });
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    onDrop: (acceptedFiles: File[]) => {
      handleFileChange(acceptedFiles[0]);
    },
  });

  const {
    palette: { mode: themePaletteMode },
  } = useTheme();
  const isCurrentThemeDark = themePaletteMode === "dark";

  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle>{title && <Typography variant="h6">{title}</Typography>}</DialogTitle>
      <DialogContent>
        <Divider />
        <form onSubmit={handleSubmit(handleSaveClick)}>
          <Typography variant="subtitle1">
            {t(
              'Select csv file with accounts. File should have the same structure as exported with delimiter "," '
            )}
          </Typography>

          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed",
              borderColor: isCurrentThemeDark ? "#666" : "#ccc",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: isDragActive
                ? isCurrentThemeDark
                  ? "#333"
                  : "#eee"
                : isCurrentThemeDark
                ? "#1e1e1e"
                : "#fafafa",
              color: isCurrentThemeDark ? "#ddd" : "#000",
              transition: "0.2s",
            }}
          >
            <Input
              id="file-input"
              type="file"
              inputProps={{ accept: ".csv", ...getInputProps() }}
              fullWidth
            />
            <Typography>
              {isDragActive ? "Drop file" : "Drag or click to choose file"}
            </Typography>

            {rawFile && (
              <Typography sx={{ mt: 2 }}>
                Chosen file: <strong>{rawFile.name}</strong>
              </Typography>
            )}
          </Box>

          {modalOpen && (
            <PreviewCsvModal isOpen={modalOpen} data={csvData} setIsOpen={setModalOpen} />
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="outlined">
          {t("cancel")}
        </Button>
        <Button
          type="submit"
          variant="contained"
          onClick={handleSubmit(handleSaveClick)}
          disabled={isOnSaveResponsePending}
        >
          {isOnSaveResponsePending ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportAccountsDialog;

function PreviewCsvModal({
  isOpen,
  setIsOpen,
  data,
}: {
  isOpen: boolean;
  data: SysAccountCreateDto[];
  setIsOpen: (open: boolean) => void;
}) {
  return (
    <Dialog open={isOpen} fullWidth>
      <DialogTitle>Preview CSV</DialogTitle>
      <DialogContent style={{ maxHeight: 400, overflow: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {Object.keys(data[0] || {}).map((key) => (
                <TableCell key={key}>{key}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, idx) => (
              <TableRow key={idx}>
                {Object.values(row).map((val, i) => (
                  <TableCell key={i}>{val?.toString()}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
